/**
 * ENG-10488 — what `agt integration probe <slug>` may truthfully say when it
 * DOES find the slug.
 *
 * ENG-10196 fixed the absent branch: when `find()` fails, the message now names
 * the list it searched and says what that list does not cover. The present
 * branch was not touched, and it has the same defect pointed the other way.
 *
 * THE DEFECT. Since ENG-7543 a toolkit can hold several connections under one
 * `definition_id`, distinguished by `connection_key`. The command resolves the
 * slug with `find()`, which takes the FIRST. The code already knows this — one
 * line in `buildForcedProbeTarget` says so: "find() picks the first connection
 * for the slug". The OUTPUT never did. It printed:
 *
 *   composio/outlook on ea: ok
 *
 * with no mention of which connection that verdict is about, or that there are
 * others.
 *
 * MEASURED IN PRODUCTION, not constructed. Agent `ea`, two `composio/outlook`
 * connections created eight minutes apart on 2026-08-04:
 *
 *   connection_key "default"  status active  ok, live OUTLOOK call succeeded
 *   connection_key "work"     status error   down, "No credentials configured",
 *                                            338 consecutive failed probes,
 *                                            an integration_down alert open
 *                                            against it since 13 September
 *
 * Probing `outlook` returns `ok`. The operator is not being lied to about the
 * row that was reached — they are being told nothing about the row that was
 * not.
 *
 * AND THE DANGEROUS SIBLING IS THE ONE THAT CANNOT BE LISTED. The candidate set
 * comes from `POST /host/agent-integrations`, which filters
 * `.in('status', ['active','configured'])`. So a sibling in `error` or
 * `unhealthy` — precisely the sibling worth knowing about — is not in the array
 * this command holds. Enumerating the siblings it CAN see would produce, for
 * `ea`, an empty list and a verdict that still reads clean.
 *
 * WHY A SENTENCE RATHER THAN A WIDER QUERY. Same reasoning ENG-10196 recorded
 * and for the same shape: widening the endpoint is a bigger change with its own
 * blast radius, and what is available here is the truth about scope. A verdict
 * that names its own scope cannot be mistaken for a wider one.
 *
 * WHY IT IS LOUD. An absent-branch bug gets investigated; a present-branch bug
 * gets believed. `ok` is the answer nobody re-reads.
 */

export interface ProbeScopeNoteInput {
  /** The slug the operator asked about, verbatim. */
  slug: string;
  /** The agent's code name. */
  agentCodeName: string;
  /** `connection_key` of the row actually probed. `null` when the row carries none. */
  connectionKey: string | null;
  /**
   * `connection_key`s of OTHER rows under the same slug that were present in the
   * candidate set. Usually empty — not because there are no siblings, but
   * because the status filter hides the ones that matter. See the header.
   */
  visibleSiblingKeys: readonly string[];
}

/** The key as it should be shown when a row carries none. */
const UNKEYED = '(no connection_key)';

/**
 * Pure, so the wording is pinned by a test rather than by whoever edits the
 * command next — the same reason `notProvisionedMessage` is pure. Returns the
 * whole multi-line note, with no trailing newline.
 */
export function probeScopeNote(input: ProbeScopeNoteInput): string {
  const key = input.connectionKey ?? UNKEYED;
  const lines = [
    `This verdict is about connection "${key}" of "${input.slug}" on ${input.agentCodeName}.`,
  ];

  if (input.visibleSiblingKeys.length > 0) {
    lines.push(
      `Other connections under this slug in the probed set: ${input.visibleSiblingKeys
        .map((k) => `"${k}"`)
        .join(', ')}. Each needs its own probe — this run covered none of them.`,
    );
  }

  lines.push(
    '',
    // The load-bearing sentence, and the reason listing siblings is not enough
    // on its own. Without it an empty sibling line reads as "there is only one".
    `It is NOT a verdict about "${input.slug}" as a whole. A toolkit can hold`,
    `several connections (ENG-7543) and this command probes the FIRST one it`,
    `resolves. Siblings in status 'error' or 'unhealthy' are filtered out of the`,
    `set this command searches, so the connection most likely to be broken is`,
    `also the one that cannot appear above. To settle what exists, read the`,
    `agent's integration rows directly rather than relying on this verdict.`,
  );

  return lines.join('\n');
}
